import { useCallback, type FormEvent, type MutableRefObject } from 'react';
import type { Timeframe } from '../types';
import { useReplayAuthGate } from './useReplayAuthGate';

export interface UseReplayAuthControllerParams {
  replayMode: 'off' | 'selecting' | 'active';
  setReplayMode: (mode: 'off' | 'selecting' | 'active') => void;
  replayModeRef: MutableRefObject<'off' | 'selecting' | 'active'>;

  liveTicker: string;
  timeframe: Timeframe;
  setLiveTicker: (ticker: string) => void;
  setTickerInput: (ticker: string) => void;
  setTimeframe: (timeframe: Timeframe) => void;

  handleFetchLiveData: (
    e?: FormEvent,
    tickerOverride?: string,
    timeframeOverride?: Timeframe,
    forceNew?: boolean
  ) => Promise<void>;

  candlesLength: number;
  isFetchingData: boolean;
  isInitialLoading: boolean;
  isRestoringSession: boolean;
}

export interface ReplayAuthController {
  ensureSignedInForReplay: (context?: { ticker?: string; timeframe?: Timeframe }) => boolean;
  isAuthLoaded: boolean;
  isSignedIn: boolean;
  isPendingReplayRedirect: boolean;
  enterReplaySelectingMode: () => void;
  cancelReplaySelectingMode: () => void;
}

export function useReplayAuthController(params: UseReplayAuthControllerParams): ReplayAuthController {
  const {
    replayMode,
    setReplayMode,
    replayModeRef,
    liveTicker,
    timeframe,
    setLiveTicker,
    setTickerInput,
    setTimeframe,
    handleFetchLiveData,
    candlesLength,
    isFetchingData,
    isInitialLoading,
    isRestoringSession,
  } = params;

  const enterReplaySelectingMode = useCallback(() => {
    setReplayMode('selecting');
    replayModeRef.current = 'selecting';
  }, [replayModeRef, setReplayMode]);

  const cancelReplaySelectingMode = useCallback(() => {
    setReplayMode('off');
    replayModeRef.current = 'off';
  }, [replayModeRef, setReplayMode]);

  const restoreReplayContext = useCallback(
    (context: { ticker?: string; timeframe?: Timeframe }) => {
      if (context.ticker) {
        const nextTicker = context.ticker.toUpperCase().trim();
        if (nextTicker) {
          setLiveTicker(nextTicker);
          setTickerInput(nextTicker);
        }
      }

      if (context.timeframe) {
        setTimeframe(context.timeframe);
      }
    },
    [setLiveTicker, setTickerInput, setTimeframe]
  );

  const autoFetchSelectingMode = useCallback(async () => {
    await handleFetchLiveData(undefined, liveTicker, timeframe);
  }, [handleFetchLiveData, liveTicker, timeframe]);

  const { ensureSignedInForReplay, isAuthLoaded, isSignedIn, isPendingReplayRedirect } = useReplayAuthGate({
    replayMode,
    enterReplaySelectingMode,
    restoreReplayContext,
    autoFetchSelectingMode,
    candlesLength,
    isFetchingData,
    isInitialLoading,
    isRestoringSession,
  });

  return {
    ensureSignedInForReplay,
    isAuthLoaded,
    isSignedIn,
    isPendingReplayRedirect,
    enterReplaySelectingMode,
    cancelReplaySelectingMode,
  };
}
